import React, { useState } from 'react';
import * as XLSX from 'xlsx';
import { collection, writeBatch, doc, Timestamp, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../../services/firebase';

interface Period {
  periodId: string;
  periodNumber: string;
  order: number;
  subject: string;
  teacherId: string;
  teacherName: string;
  startTime: string;
  endTime: string;
  duration: number;
  isBreak: boolean;
  type: string;
}

interface DayTimetable {
  academicYear: string;
  classId: string;
  className: string;
  day: string;
  date: string;
  periods: Period[];
  createdAt: Timestamp;
  createdBy: string;
}

interface ClassTimetable {
  classId: string;
  section: string;
  className: string;
  schedule: { [day: string]: Period[] };
}

interface ValidationError {
  sheet?: string;
  row?: number;
  message: string;
}

interface TimeTableUploadProps {
  domain: string;
}

const TimeTableUpload: React.FC<TimeTableUploadProps> = ({ domain }) => {
  const [uploadMode, setUploadMode] = useState<'single' | 'bulk'>('single');
  const [file, setFile] = useState<File | null>(null);
  const [timetables, setTimetables] = useState<ClassTimetable[]>([]);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadComplete, setUploadComplete] = useState(false);
  const [uploadedCount, setUploadedCount] = useState(0);
  const [academicYear, setAcademicYear] = useState<string>('2025-26');
  const [startDate, setStartDate] = useState<string>('2025-10-10');

  const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  // Helper function to calculate duration in minutes
  const calculateDuration = (startTime: string, endTime: string): number => {
    const [startHour, startMin] = startTime.split(':').map(Number);
    const [endHour, endMin] = endTime.split(':').map(Number);
    return (endHour * 60 + endMin) - (startHour * 60 + startMin);
  };

  // Function to update teacher schedules after timetable upload
  const updateTeacherSchedulesAfterUpload = async (
    uploadedTimetables: ClassTimetable[],
    getDateForDay: (day: string, startDate: string) => string
  ) => {
    try {
      console.log('🔄 Updating teacher schedules after upload...');

      // Collect all teacher periods grouped by teacherId and day
      const teacherScheduleMap: Map<string, {
        teacherId: string;
        teacherName: string;
        day: string;
        date: string;
        periods: any[];
      }> = new Map();

      uploadedTimetables.forEach(timetable => {
        Object.entries(timetable.schedule).forEach(([day, periods]) => {
          const dayDate = getDateForDay(day, startDate);

          periods.forEach(period => {
            if (!period.isBreak && period.teacherId) {
              const key = `${period.teacherId}_${day}`;

              if (!teacherScheduleMap.has(key)) {
                teacherScheduleMap.set(key, {
                  teacherId: period.teacherId,
                  teacherName: period.teacherName,
                  day: day,
                  date: dayDate,
                  periods: [],
                });
              }

              const schedule = teacherScheduleMap.get(key)!;
              schedule.periods.push({
                ...period,
                classId: timetable.classId,
                className: timetable.className,
              });
            }
          });
        });
      });

      // Now save each teacher's schedule
      for (const [key, scheduleData] of teacherScheduleMap) {
        const scheduleRef = doc(db, `school-users/${domain}/teacher-schedules`, key);

        // Get existing schedule to merge with
        const existingDoc = await getDoc(scheduleRef);
        let existingPeriods: any[] = [];

        if (existingDoc.exists()) {
          existingPeriods = existingDoc.data().periods || [];
          // Remove periods for classes being uploaded (to avoid duplicates)
          const uploadedClassIds = uploadedTimetables.map(t => t.classId);
          existingPeriods = existingPeriods.filter(
            (p: any) => !uploadedClassIds.includes(p.classId)
          );
        }

        // Combine and sort periods
        const allPeriods = [...existingPeriods, ...scheduleData.periods];
        allPeriods.sort((a, b) => {
          const timeA = a.startTime?.split(':').map(Number) || [0, 0];
          const timeB = b.startTime?.split(':').map(Number) || [0, 0];
          return (timeA[0] * 60 + timeA[1]) - (timeB[0] * 60 + timeB[1]);
        });

        await setDoc(scheduleRef, {
          teacherId: scheduleData.teacherId,
          teacherName: scheduleData.teacherName,
          day: scheduleData.day,
          date: scheduleData.date,
          periods: allPeriods,
          lastUpdated: Timestamp.now(),
          ...(existingDoc.exists() ? {} : { createdAt: Timestamp.now() }),
        }, { merge: true });
      }

      console.log(`✅ Updated ${teacherScheduleMap.size} teacher schedule entries`);
    } catch (error) {
      console.error('❌ Error updating teacher schedules:', error);
      // Don't throw - we don't want to fail the whole upload if teacher schedule update fails
    }
  };

  // Maximum number of periods supported
  const MAX_PERIODS = 20;

  const downloadSingleClassTemplate = () => {
    const templateData: any[] = [];

    // Add days with sample data
    days.forEach(day => {
      const row: any = { Day: day };
      for (let i = 1; i <= MAX_PERIODS; i++) {
        const isBreak = i === 5 || i === 10 || i === 15; // Multiple breaks for longer schedules
        const startHour = 7 + Math.floor((i - 1) * 0.75); // Approximate timing
        const startMin = ((i - 1) * 45) % 60;
        const endMinutes = isBreak ? '30' : '45';
        row[`P${i} Subject`] = isBreak ? 'BREAK' : (i === 1 ? 'Preparatory' : i === 2 ? 'Assembly' : 'Mathematics');
        row[`P${i} Teacher Name`] = isBreak ? '' : 'John Doe';
        row[`P${i} Teacher ID`] = isBreak ? '' : '17PVSS0001';
        row[`P${i} Time`] = `${startHour}:${startMin.toString().padStart(2, '0')}-${startHour}:${endMinutes}`;
        row[`P${i} Type`] = 'Regular';
      }
      templateData.push(row);
    });

    const ws = XLSX.utils.json_to_sheet(templateData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Class1A');

    // Set column widths
    const colWidths = [{ wch: 12 }]; // Day column
    for (let i = 0; i < MAX_PERIODS; i++) {
      colWidths.push({ wch: 20 }, { wch: 18 }, { wch: 15 }, { wch: 15 }, { wch: 12 });
    }
    ws['!cols'] = colWidths;

    XLSX.writeFile(wb, 'timetable_single_class_template.xlsx');
  };

  const downloadBulkTemplate = () => {
    const wb = XLSX.utils.book_new();

    // Create sheets for multiple classes
    const classes = ['Class1A', 'Class1B', 'Class2A', 'Class2B'];

    classes.forEach(className => {
      const templateData: any[] = [];

      days.forEach(day => {
        const row: any = { Day: day };
        for (let i = 1; i <= MAX_PERIODS; i++) {
          const isBreak = i === 5 || i === 10 || i === 15; // Multiple breaks for longer schedules
          const startHour = 7 + Math.floor((i - 1) * 0.75); // Approximate timing
          const startMin = ((i - 1) * 45) % 60;
          const endMinutes = isBreak ? '30' : '45';
          row[`P${i} Subject`] = isBreak ? 'BREAK' : (i === 1 ? 'Preparatory' : i === 2 ? 'Assembly' : 'Mathematics');
          row[`P${i} Teacher Name`] = isBreak ? '' : 'John Doe';
          row[`P${i} Teacher ID`] = isBreak ? '' : '17PVSS0001';
          row[`P${i} Time`] = `${startHour}:${startMin.toString().padStart(2, '0')}-${startHour}:${endMinutes}`;
          row[`P${i} Type`] = 'Regular';
        }
        templateData.push(row);
      });

      const ws = XLSX.utils.json_to_sheet(templateData);

      // Set column widths
      const colWidths = [{ wch: 12 }];
      for (let i = 0; i < MAX_PERIODS; i++) {
        colWidths.push({ wch: 20 }, { wch: 18 }, { wch: 15 }, { wch: 15 }, { wch: 12 });
      }
      ws['!cols'] = colWidths;

      XLSX.utils.book_append_sheet(wb, ws, className);
    });

    XLSX.writeFile(wb, 'timetable_bulk_upload_template.xlsx');
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      setTimetables([]);
      setValidationErrors([]);
      setUploadComplete(false);
      parseExcel(selectedFile);
    }
  };

  const parseExcel = (file: File) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });

        const parsedTimetables: ClassTimetable[] = [];
        const errors: ValidationError[] = [];

        if (uploadMode === 'single') {
          // Parse single sheet
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet);

          const timetable = parseSingleTimetable(sheetName, jsonData, errors);
          if (timetable) {
            parsedTimetables.push(timetable);
          }
        } else {
          // Parse multiple sheets (bulk mode)
          workbook.SheetNames.forEach(sheetName => {
            const worksheet = workbook.Sheets[sheetName];
            const jsonData = XLSX.utils.sheet_to_json(worksheet);

            const timetable = parseSingleTimetable(sheetName, jsonData, errors);
            if (timetable) {
              parsedTimetables.push(timetable);
            }
          });
        }

        setTimetables(parsedTimetables);
        setValidationErrors(errors);

        if (errors.length > 0) {
          console.warn('Validation errors found:', errors);
        } else {
          console.log('All timetables validated successfully');
        }
      } catch (error) {
        console.error('Error parsing Excel file:', error);
        setValidationErrors([{ message: 'Failed to parse Excel file. Please check the format.' }]);
      }
    };

    reader.readAsArrayBuffer(file);
  };

  const parseSingleTimetable = (sheetName: string, jsonData: any[], errors: ValidationError[]): ClassTimetable | null => {
    // Extract class and section from sheet name
    // Supports formats: "Class1A", "1A", "Class 1A"
    const classMatch = sheetName.match(/class\s*(\d+)([A-Z])/i) || sheetName.match(/^(\d+)([A-Z])$/i);

    if (!classMatch) {
      errors.push({ sheet: sheetName, message: 'Invalid sheet name format. Use "Class1A" or "1A"' });
      return null;
    }

    const classNum = classMatch[1];
    const section = classMatch[2].toUpperCase();
    const classId = `class${classNum}${section.toLowerCase()}`; // e.g., "class1a"
    const className = `Class ${classNum}${section}`; // e.g., "Class 1A"

    const schedule: { [day: string]: Period[] } = {};

    jsonData.forEach((row: any, index: number) => {
      const day = String(row['Day'] || '').trim();

      if (!days.includes(day)) {
        errors.push({ sheet: sheetName, row: index + 2, message: `Invalid day: ${day}` });
        return;
      }

      const periods: Period[] = [];

      // Parse periods (up to 20 periods)
      for (let i = 1; i <= MAX_PERIODS; i++) {
        const subject = String(row[`P${i} Subject`] || '').trim();
        const teacherName = String(row[`P${i} Teacher Name`] || '').trim();
        const teacherId = String(row[`P${i} Teacher ID`] || '').trim();
        const type = String(row[`P${i} Type`] || 'Regular').trim();

        // Support both formats:
        // Format 1: Separate columns "P1 Start Time" and "P1 End Time"
        // Format 2: Single column "P1 Time" with range like "9:00-9:45"
        let startTime = String(row[`P${i} Start Time`] || '').trim();
        let endTime = String(row[`P${i} End Time`] || '').trim();

        // If separate columns are empty, try the combined "Time" column
        if (!startTime && !endTime) {
          const timeRange = String(row[`P${i} Time`] || '').trim();
          if (timeRange && timeRange.includes('-')) {
            const [start, end] = timeRange.split('-').map(t => t.trim());
            startTime = start;
            endTime = end;
          }
        }

        if (subject) {
          const isBreak = subject.toUpperCase() === 'BREAK';
          const duration = startTime && endTime ? calculateDuration(startTime, endTime) : 0;

          periods.push({
            periodId: `period_${i}`,
            periodNumber: `Period ${i}`,
            order: i,
            subject: subject,
            teacherId: teacherId,
            teacherName: teacherName,
            startTime: startTime,
            endTime: endTime,
            duration: duration,
            isBreak: isBreak,
            type: type,
          });
        }
      }

      if (periods.length > 0) {
        schedule[day] = periods;
      }
    });

    // Validate that we have at least some days
    if (Object.keys(schedule).length === 0) {
      errors.push({ sheet: sheetName, message: 'No valid schedule data found' });
      return null;
    }

    return { classId, section, className, schedule };
  };

  const uploadTimetables = async () => {
    if (validationErrors.length > 0) {
      alert('Please fix validation errors before uploading');
      return;
    }

    if (timetables.length === 0) {
      alert('No timetables to upload');
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setUploadedCount(0);

    try {
      const batch = writeBatch(db);
      let totalUploaded = 0;

      // Helper to calculate date for each day
      const getDateForDay = (dayName: string, startDate: string): string => {
        const dayMap: {[key: string]: number} = {
          'Monday': 1, 'Tuesday': 2, 'Wednesday': 3,
          'Thursday': 4, 'Friday': 5, 'Saturday': 6
        };
        const baseDate = new Date(startDate);
        const currentDay = baseDate.getDay() || 7; // 0 = Sunday, convert to 7
        const targetDay = dayMap[dayName];
        const daysToAdd = (targetDay - currentDay + 7) % 7;
        const resultDate = new Date(baseDate);
        resultDate.setDate(baseDate.getDate() + daysToAdd);
        return resultDate.toISOString().split('T')[0]; // YYYY-MM-DD format
      };

      timetables.forEach(timetable => {
        // Create a document for each day
        Object.entries(timetable.schedule).forEach(([day, periods]) => {
          const docId = `${timetable.classId}_${day.toLowerCase()}`;
          const docRef = doc(collection(db, `school-users/${domain}/timetables`), docId);

          const dayDate = getDateForDay(day, startDate);

          const timetableData: DayTimetable = {
            academicYear: academicYear,
            classId: timetable.classId,
            className: timetable.className,
            day: day,
            date: dayDate,
            periods: periods,
            createdAt: Timestamp.now(),
            createdBy: 'admin_excel_upload',
          };

          batch.set(docRef, timetableData);
          totalUploaded++;
        });
      });

      await batch.commit();

      // Update teacher schedules after timetable upload
      setUploadProgress(80);
      await updateTeacherSchedulesAfterUpload(timetables, getDateForDay);

      setUploadedCount(totalUploaded);
      setUploadProgress(100);
      setUploadComplete(true);
      console.log(`✅ Successfully uploaded ${totalUploaded} timetable entries and updated teacher schedules`);
    } catch (error: any) {
      console.error('Error uploading timetables:', error);
      alert(`Failed to upload timetables: ${error.message}`);
    } finally {
      setUploading(false);
    }
  };

  const reset = () => {
    setFile(null);
    setTimetables([]);
    setValidationErrors([]);
    setUploadComplete(false);
    setUploadProgress(0);
    setUploadedCount(0);
  };

  return (
    <div className="card shadow-sm">
      <div className="card-header bg-white py-3">
        <h5 className="mb-0">
          <i className="bi bi-calendar3 me-2 text-primary-custom"></i>
          Upload Class Timetables
        </h5>
        <small className="text-muted">Upload timetables for one or multiple classes</small>
      </div>
      <div className="card-body">
        {/* Upload Mode Selection */}
        <div className="mb-4">
          <label className="form-label fw-semibold">Upload Mode</label>
          <div className="btn-group w-100" role="group">
            <button
              type="button"
              className={`btn ${uploadMode === 'single' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => {
                setUploadMode('single');
                reset();
              }}
            >
              <i className="bi bi-file-earmark me-2"></i>
              Single Class
            </button>
            <button
              type="button"
              className={`btn ${uploadMode === 'bulk' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => {
                setUploadMode('bulk');
                reset();
              }}
            >
              <i className="bi bi-files me-2"></i>
              Bulk Upload (Multiple Classes)
            </button>
          </div>
          <small className="text-muted d-block mt-2">
            {uploadMode === 'single'
              ? 'Upload timetable for one class with one Excel sheet'
              : 'Upload timetables for multiple classes using separate sheets in one Excel file'}
          </small>
        </div>

        {/* Academic Year and Start Date Inputs */}
        <div className="row mb-4">
          <div className="col-md-6">
            <label className="form-label fw-semibold">
              <i className="bi bi-calendar-event me-2"></i>
              Academic Year
            </label>
            <input
              type="text"
              className="form-control"
              placeholder="e.g., 2025-26"
              value={academicYear}
              onChange={(e) => setAcademicYear(e.target.value)}
              disabled={uploading}
            />
            <small className="text-muted">Format: YYYY-YY</small>
          </div>
          <div className="col-md-6">
            <label className="form-label fw-semibold">
              <i className="bi bi-calendar-date me-2"></i>
              Start Date (for calculating week dates)
            </label>
            <input
              type="date"
              className="form-control"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              disabled={uploading}
            />
            <small className="text-muted">Used to calculate dates for each day</small>
          </div>
        </div>

        {/* Download Template Section */}
        <div className="alert alert-info d-flex align-items-center mb-4">
          <i className="bi bi-info-circle fs-4 me-3"></i>
          <div className="flex-grow-1">
            <strong>Step 1:</strong> Download the Excel template, fill in the timetable details, and upload.
            <br />
            <small>
              {uploadMode === 'single'
                ? 'Template includes one sheet with days and periods.'
                : 'Template includes multiple sheets (one per class). You can add/remove sheets as needed.'}
            </small>
          </div>
          <button
            className="btn btn-primary"
            onClick={uploadMode === 'single' ? downloadSingleClassTemplate : downloadBulkTemplate}
          >
            <i className="bi bi-download me-2"></i>
            Download Template
          </button>
        </div>

        {/* Instructions */}
        <div className="alert alert-warning mb-4">
          <h6 className="alert-heading">
            <i className="bi bi-lightbulb me-2"></i>
            Template Instructions
          </h6>
          <ul className="mb-0 small">
            <li><strong>Sheet Naming:</strong> Name sheets as "1A", "1B", "2A" or "Class1A", "Class1B" (Class Number + Section Letter)</li>
            <li><strong>Days:</strong> Use exact names: Monday, Tuesday, Wednesday, Thursday, Friday, Saturday</li>
            <li><strong>Column Format:</strong> For each period (P1-P20), fill in:
              <ul className="mt-1">
                <li><code>P1 Subject</code> - Subject name (e.g., "Mathematics", "BREAK")</li>
                <li><code>P1 Teacher Name</code> - Full teacher name (e.g., "Mr. John Doe")</li>
                <li><code>P1 Teacher ID</code> - Teacher's employee ID (e.g., "17PVSS0001")</li>
                <li><code>P1 Time</code> - Time range (e.g., "9:00-9:45") OR use separate columns:</li>
                <li style={{ marginLeft: '20px' }}><code>P1 Start Time</code> and <code>P1 End Time</code></li>
                <li><code>P1 Type</code> - Period type (e.g., "Regular", "Lab", "Sports")</li>
              </ul>
            </li>
            <li><strong>Breaks:</strong> Use "BREAK" as subject, leave Teacher Name and Teacher ID empty</li>
            <li><strong>Time Format:</strong> Use range format "9:00-9:45" or separate times in 24-hour format</li>
          </ul>
        </div>

        {/* File Upload Section */}
        <div className="mb-4">
          <label className="form-label fw-semibold">
            <i className="bi bi-file-earmark-excel me-2"></i>
            Step 2: Upload Excel File
          </label>
          <input
            type="file"
            className="form-control"
            accept=".xlsx,.xls"
            onChange={handleFileChange}
            disabled={uploading}
          />
          {file && (
            <small className="text-muted d-block mt-2">
              <i className="bi bi-check-circle text-success me-1"></i>
              Selected: {file.name}
            </small>
          )}
        </div>

        {/* Validation Errors */}
        {validationErrors.length > 0 && (
          <div className="alert alert-danger">
            <h6 className="alert-heading">
              <i className="bi bi-exclamation-triangle me-2"></i>
              Validation Errors ({validationErrors.length})
            </h6>
            <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
              <table className="table table-sm mb-0">
                <thead>
                  <tr>
                    <th>Sheet</th>
                    <th>Row</th>
                    <th>Error</th>
                  </tr>
                </thead>
                <tbody>
                  {validationErrors.map((error, idx) => (
                    <tr key={idx}>
                      <td>{error.sheet || '-'}</td>
                      <td>{error.row || '-'}</td>
                      <td>{error.message}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Preview Section */}
        {timetables.length > 0 && validationErrors.length === 0 && (
          <div className="mb-4">
            <h6 className="fw-semibold mb-3">
              <i className="bi bi-eye me-2 text-success"></i>
              Preview ({timetables.length} {timetables.length === 1 ? 'class' : 'classes'})
            </h6>
            {timetables.map((timetable, idx) => (
              <div key={idx} className="card mb-3">
                <div className="card-header bg-light">
                  <strong>{timetable.className}</strong>
                  <span className="badge bg-primary ms-2">
                    {Object.keys(timetable.schedule).length} days
                  </span>
                </div>
                <div className="card-body">
                  {/* Calculate max periods across all days for this timetable */}
                  {(() => {
                    const maxPeriods = Math.max(...Object.values(timetable.schedule).map(p => p.length));
                    return (
                      <div className="table-responsive" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                        <table className="table table-sm table-bordered">
                          <thead className="table-light sticky-top">
                            <tr>
                              <th>Day</th>
                              {Array.from({ length: maxPeriods }).map((_, i) => (
                                <th key={i}>Period {i + 1}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {Object.entries(timetable.schedule).map(([day, periods]) => (
                              <tr key={day}>
                                <td className="fw-bold">{day}</td>
                                {periods.map((period, pIdx) => (
                                  <td key={pIdx} className="small">
                                    <div><strong>{period.subject}</strong></div>
                                    <div className="text-muted">{period.teacherName}</div>
                                    <div className="text-muted small">{period.startTime} - {period.endTime}</div>
                                    <div className="text-muted small">({period.duration}m)</div>
                                  </td>
                                ))}
                                {/* Fill empty cells if less than max periods */}
                                {Array.from({ length: Math.max(0, maxPeriods - periods.length) }).map((_, i) => (
                                  <td key={`empty-${i}`}>-</td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    );
                  })()}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Upload Progress */}
        {uploading && (
          <div className="mb-4">
            <div className="d-flex justify-content-between mb-2">
              <span>Uploading timetables...</span>
              <span>{uploadedCount} entries</span>
            </div>
            <div className="progress" style={{ height: '25px' }}>
              <div
                className="progress-bar progress-bar-striped progress-bar-animated"
                role="progressbar"
                style={{ width: `${uploadProgress}%` }}
              >
                {Math.round(uploadProgress)}%
              </div>
            </div>
          </div>
        )}

        {/* Success Message */}
        {uploadComplete && (
          <div className="alert alert-success">
            <i className="bi bi-check-circle me-2"></i>
            Successfully uploaded timetables for {timetables.length} {timetables.length === 1 ? 'class' : 'classes'}
            ({uploadedCount} total entries)!
          </div>
        )}

        {/* Action Buttons */}
        <div className="d-flex gap-2">
          {!uploadComplete && timetables.length > 0 && validationErrors.length === 0 && (
            <button
              className="btn btn-success"
              onClick={uploadTimetables}
              disabled={uploading}
            >
              {uploading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                  Uploading...
                </>
              ) : (
                <>
                  <i className="bi bi-cloud-upload me-2"></i>
                  Upload {timetables.length} {timetables.length === 1 ? 'Timetable' : 'Timetables'}
                </>
              )}
            </button>
          )}
          {(timetables.length > 0 || uploadComplete) && (
            <button className="btn btn-secondary" onClick={reset} disabled={uploading}>
              <i className="bi bi-arrow-counterclockwise me-2"></i>
              Reset
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default TimeTableUpload;
